/* eslint-disable */
import { AxiosError } from 'axios';

import { IconName } from 'components/atoms/Icon';
import { AccountConversationDataType, AccountConversationGroupType } from 'services/accountConversations/types';
import { TAG_STATUS } from 'utils/constants';

function mapModifiers(baseClassName: string, ...modifiers: (string | string[] | false | undefined)[]): string {
  return modifiers
    .reduce<string[]>((acc, m) => (!m ? acc : [...acc, ...(typeof m === 'string' ? [m] : m)]), [])
    .map((m) => `-${m}`)
    .reduce<string>((classNames, suffix) => `${classNames} ${baseClassName}${suffix}`, baseClassName);
}

export default mapModifiers;

/*!
 * Scroll down to next block element
 */
export function scrollDownNextSection(ref: React.RefObject<HTMLDivElement>) {
  if (ref && ref.current) {
    window.scrollTo({ behavior: 'smooth', top: ref.current.offsetTop - 68 }); // Minus header height
  }
}

/*!
 * getMousePosition(event) - cross browser normalizing of:
 * clientX, clientY, screenX, screenY, offsetX, offsetY, pageX, pageY
 * HTMLElement
 */
export function getMousePosition(
  evt: React.MouseEvent<SVGPathElement, MouseEvent> | React.MouseEvent<SVGRectElement, MouseEvent>,
  item: HTMLDivElement
) {
  let { pageX } = evt;
  let { pageY } = evt;
  if (pageX === undefined) {
    pageX = evt.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
    pageY = evt.clientY + document.body.scrollTop + document.documentElement.scrollTop;
  }

  const rect = item.getBoundingClientRect();
  const offsetX = evt.clientX - rect.left;
  const offsetY = evt.clientY - rect.top;

  return {
    client: { x: evt.clientX, y: evt.clientY }, // relative to the viewport
    screen: { x: evt.screenX, y: evt.screenY }, // relative to the physical screen
    offset: { x: offsetX, y: offsetY }, // relative to the event target
    page: { x: pageX, y: pageY }, // relative to the html document
  };
}

export function getDimensions(ele: HTMLDivElement) {
  const { height } = ele.getBoundingClientRect();
  const { offsetTop } = ele;
  const offsetBottom = offsetTop + height;

  return {
    height,
    offsetTop,
    offsetBottom,
  };
}

export function scrollStop(callback: (value: any) => void, time = 2000) {
  // Make sure a valid callback was provided
  if (!callback || typeof callback !== 'function') return;

  // Setup scrolling variable
  let isScrolling: any;

  // Listen for scroll events
  window.addEventListener(
    'scroll',
    () => {
      // Clear our timeout throughout the scroll
      window.clearTimeout(isScrolling);

      // Set a timeout to run after scrolling ends
      isScrolling = setTimeout(callback, time);
    },
    false
  );
}

export function formatDate(date: Date) {
  const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export const formatNumber = (value = 0) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const formatDateTime = (time?: string): string | undefined => {
  if (!time) return undefined;

  const dateData = new Date(time);
  const date = dateData.getDate() < 10 ? `0${dateData.getDate()}` : dateData.getDate();
  const months = dateData.getMonth() + 1 < 10 ? `0${dateData.getMonth() + 1}` : dateData.getMonth() + 1;
  const year = dateData.getFullYear().toString();

  return `${date}/${months}/${year}`;
};

export const formatDateTimeToHHMM = (time?: string): string | undefined => {
  if (!time) return undefined;

  const dateData = new Date(time);
  const date = dateData.getDate() < 10 ? `0${dateData.getDate()}` : dateData.getDate();
  const months = dateData.getMonth() + 1 < 10 ? `0${dateData.getMonth() + 1}` : dateData.getMonth() + 1;
  const year = dateData.getFullYear().toString();
  const hours = dateData.getHours() < 10 ? `0${dateData.getHours()}` : dateData.getHours();
  const minutes = dateData.getMinutes() < 10 ? `0${dateData.getMinutes()}` : dateData.getMinutes();

  return `${date}/${months}/${year} ${hours}:${minutes}`;
};

export const formatDateTimeDayOfWeek = (inputDate?: string) => {
  if (!inputDate) {
    return '';
  }
  const date = new Date(inputDate);

  date.setHours(date.getHours() + 7);

  const dayOfWeek = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
  const thurX = dayOfWeek[date.getUTCDay()];

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();

  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');

  return `${thurX}, ${day}/${month}/${year} ${hours}:${minutes}`;
};

export const BASE_URL = process.env.REACT_APP_BASE_URL;

export const baseURL = (src?: string): string => `${BASE_URL || ''}${src || ''}`;

export const mappingURLToExternal = (path?: string) => (path ? new URL(path, window.location.origin).href : '');

export const checkExternalUrl = (str?: string) => {
  if (!str) return false;
  const tareaRegex = /^(http|https|tel)/;
  return tareaRegex.test(String(str).toLowerCase());
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const getFirstChar = (text: string) => text.charAt(0);

export const getFirstLetters = (str: string) => {
  const strSplit = str.split(' ');
  if (strSplit.length > 1) {
    const firstLetter = getFirstChar(strSplit[0]);
    const lastLetter = getFirstChar(strSplit[strSplit.length - 1]);
    return firstLetter + lastLetter;
  }
  return strSplit
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

export function formatBytes(a: number, b = 2) {
  if (!+a) return '0 Bytes';
  const c = b < 0 ? 0 : b;
  const d = Math.floor(Math.log(a) / Math.log(1024));
  return `${parseFloat((a / 1024 ** d).toFixed(c))} ${['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][d]}`;
}

// export function errorFileUpload(error?: {
//   type: string,
//   message: string
// }) {
//   if (error?.type === INVALID_FILES.MAX_SIZE_UPLOAD) {
//     return error?.message || 'File exceeds max size';
//   }
//   if (error?.type === INVALID_FILES.NOT_SUPPORTED_TYPE) {
//     return error?.message || 'File type not supported';
//   }
//   if (error?.type === INVALID_FILES.ERROR_UPLOAD) {
//     return error?.message || '';
//   }
//   return error?.message || '';
// }

export const handleStatus = (status: number): { message: string; value: keyof typeof TAG_STATUS } => {
  switch (status) {
    case 1:
      return {
        message: 'Complete',
        value: TAG_STATUS.SUCCESS as keyof typeof TAG_STATUS,
      };
    case 2:
      return {
        message: 'Processing',
        value: TAG_STATUS.WARNING as keyof typeof TAG_STATUS,
      };
    case 3:
      return {
        message: 'Error',
        value: TAG_STATUS.ERROR as keyof typeof TAG_STATUS,
      };
    default:
      return {
        message: '',
        value: TAG_STATUS.DEFAULT as keyof typeof TAG_STATUS,
      };
  }
};

export const handleType = (status: number) => {
  switch (status) {
    case 1:
      return 'Website';
    case 2:
      return 'Files';
    case 3:
      return 'Sharepoint';
    default:
      return '';
  }
};

export const handleIconType = (status: number): IconName => {
  switch (status) {
    case 2:
      return 'folder';
    case 3:
      return 'paperclip';
    default:
      return 'earth';
  }
};

export const checkTypeErrors = (error: unknown): Array<ErrorAPIResponseTypes> => {
  const errors =
    error instanceof AxiosError && error.response?.data
      ? (error.response?.data as any)?.errors
      : Array.isArray(error)
        ? error
        : false;

  if (errors && Array.isArray(errors)) {
    return errors;
  }
  return [];
};

export const getErrors = (error: unknown) => {
  const message: string[] = [];
  const errors = checkTypeErrors(error);
  console.log('errors: ', errors);

  if (errors && errors.length > 0) {
    errors.forEach((item: ErrorAPIResponseTypes) => {
      message.push(item?.message || item?.title);
    });
    return message.join(', ');
  }
  return 'Failed. Please try again later.';
};

export const compressImageFile = (file: File, big?: boolean) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxWidth = big ? 1024 : 768;
      const maxHeight = big ? 1626 : 1220;

      // Calculate new dimensions
      let { width } = img;
      let { height } = img;
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;
        if (width > height) {
          width = maxWidth;
          height = maxWidth / aspectRatio;
        } else {
          height = maxHeight;
          width = maxHeight * aspectRatio;
        }
      }

      // Set canvas dimensions and draw image
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert the canvas to a Blob and create a new File object
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Compression failed.'));
          }
        },
        'image/jpeg', // Output file format
        0.7 // Compression quality (0 to 1)
      );
    };

    img.onerror = (error) => reject(error);
  });

export const groupDataByDate = (data: AccountConversationDataType[]): AccountConversationGroupType[] => {
  const now = new Date();
  const today = now.toISOString().split('T')[0];

  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const groups: Record<string, AccountConversationDataType[]> = {
    'Hôm nay': [],
    'Trong tuần': [],
    'Trong tháng': [],
    'Trước đó': [],
  };

  data.forEach((item) => {
    const itemDate = new Date(item.updatedAt);
    const itemDay = itemDate.toISOString().split('T')[0];

    if (itemDay === today) {
      groups['Hôm nay'].push(item);
    } else if (itemDate >= startOfWeek) {
      groups['Trong tuần'].push(item);
    } else if (itemDate >= startOfMonth) {
      groups['Trong tháng'].push(item);
    } else {
      groups['Trước đó'].push(item);
    }
  });

  return Object.entries(groups).map(([label, dataList]) => ({ label, dataList }));
};

export const getFileExtension = (filename: string) => filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);

export const detectFileType = (type?: string) => {
  if (!type) return '';
  const splitArr = type.split('.');
  const extension = splitArr[splitArr.length - 1];
  if (extension.includes('sheet')) {
    return 'Excel';
  } else if (extension.includes('presentation')) {
    return 'Power Point';
  } else if (extension.includes('pdf')) {
    return 'PDF';
  } else if (extension.includes('plain')) {
    return 'TXT';
  } else if (extension.includes('image')) {
    return 'Image';
  }
  else {
    return extension;
  }
};

export const base64ToFile = (base64String: string, fileName = 'capture.png'): File => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

export const scrollBottom = (elementRef: HTMLDivElement | null) => {
  setTimeout(() => {
    const element = elementRef;
    if (element) {
      elementRef.scroll({ top: element.scrollHeight, behavior: 'smooth' });
    }
  }, 300);
};

type ClassValue = string | boolean | null | undefined | ClassObject | ClassArray;
interface ClassObject {
  [key: string]: boolean | null | undefined;
}
interface ClassArray extends Array<ClassValue> { }

export function clsx(...args: ClassValue[]): string {
  let i = 0;
  let tmp;
  let str = '';
  let len = args.length;

  for (; i < len; i++) {
    if ((tmp = args[i])) {
      if (typeof tmp === 'string') {
        str += (str && ' ') + tmp;
      } else if (Array.isArray(tmp)) {
        const nested = clsx(...tmp);
        if (nested) str += (str && ' ') + nested;
      } else if (typeof tmp === 'object') {
        for (const key in tmp) {
          if (Object.prototype.hasOwnProperty.call(tmp, key) && tmp[key]) {
            str += (str && ' ') + key;
          }
        }
      }
    }
  }

  return str;
}

export const getTextFromHTML = (html: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  temp.querySelectorAll('span').forEach((span) => span.remove());

  temp.querySelectorAll('br').forEach((br) => br.replaceWith('\n\n'));

  const blockTags = ['div', 'p', 'li', 'section', 'article', 'header', 'footer'];
  blockTags.forEach((tag) => {
    temp.querySelectorAll(tag).forEach((el) => {
      el.insertAdjacentText('afterend', '\n\n');
    });
  });

  return temp.textContent?.trim() || '';
};

export const detectErrorByCode = (code: string, field?: string, errMess?: string) => {
  switch (code) {
    case 'idCardInvalid':
      return 'CMND/CCCD không hợp lệ';
    case 'unique':
      if (field === 'idCardNumber') return 'CMND/CCCD đã tồn tại';
      return 'Dữ liệu đã tồn tại';
    case 'regex':
      if (field === 'taxCode') return 'Mã số thuế không hợp lệ';
      return 'Dữ liệu đã tồn tại';
    default:
      return errMess || 'Đã có lỗi xảy ra';
  }
};

export const formatDateDDMMYYYY = (date?: string | Date, concatChar?: string) => {
  if (!date) return '';
  const dateFormat = typeof (date) === 'string' ? new Date(date) : date;
  let day: string | number = dateFormat.getDate();
  let month: string | number = dateFormat.getMonth() + 1;
  if (day < 10) {
    day = `0${day}`;
  }
  if (month < 10) {
    month = `0${month}`;
  }
  return concatChar ? `${day}${concatChar}${month}${concatChar}${dateFormat.getFullYear()}` : `${day}-${month}-${dateFormat.getFullYear()}`;
};

export function removeVietnameseDiacriticsAndUppercase(text?: string): string {
  if (!text) return '';

  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toUpperCase();
}
